/**
 * Per-card-type one-line summary for the past-state collapsed view.
 *
 * When a card transitions to past-state (confirmed / cancelled / superseded /
 * past-gate), CardShell collapses by default and shows this summary in place
 * of the full body. Click to re-expand.
 *
 * Summaries pull the salient identifying field(s) per card type so a user
 * scrolling back through chat history can read "what was this card about"
 * without expanding. New card types should add a case here; the fallback
 * is the chip label (already in the header) so an unmissed type degrades
 * to a useful-but-minimal one-liner.
 */
import { CardType } from '../../types';

interface MinimalNamed {
  name?: unknown;
}

function tryStr(v: unknown): string | null {
  if (typeof v === 'string' && v.trim().length > 0) return v;
  return null;
}

function outputName(content: Record<string, unknown>): string | null {
  const output = content.output as
    | { selected?: MinimalNamed | null }
    | null
    | undefined;
  if (!output || !output.selected) return null;
  return tryStr((output.selected as MinimalNamed).name);
}

function segmentCount(content: Record<string, unknown>): number {
  const segments = content.segments;
  if (Array.isArray(segments)) return segments.length;
  return 0;
}

export function summarizeForCollapse(
  cardType: CardType,
  content: Record<string, unknown>
): string | null {
  switch (cardType) {
    case 'plan_confirmation_card': {
      const tool = tryStr(content.tool);
      return tool ? `Tool: ${tool}` : null;
    }
    case 'chart_visualization_param_card': {
      const out = outputName(content);
      const timescale = tryStr(content.timescale);
      if (!out) return null;
      return timescale ? `${out} · ${timescale}` : out;
    }
    case 'driver_analysis_param_card': {
      const out = outputName(content);
      const start = tryStr(content.start_date);
      const end = tryStr(content.end_date);
      const range = start && end ? `${start} → ${end}` : null;
      if (!out) return range;
      return range ? `${out} · ${range}` : out;
    }
    case 'edit_scenarios_input_selection_card': {
      const n = segmentCount(content);
      return n > 0 ? `${n} segment${n === 1 ? '' : 's'} selected` : null;
    }
    case 'edit_scenarios_change_review_card': {
      const patches = content.patch_specs;
      if (Array.isArray(patches)) {
        return `${patches.length} patch${patches.length === 1 ? '' : 'es'} to apply`;
      }
      return 'Changes pending';
    }
    case 'extracts_param_card': {
      const t = tryStr(content.extract_type);
      return t ? `Extract: ${t}` : null;
    }
    case 'scope_proposal_card': {
      const envelope = content.scope_envelope as
        | { entries?: unknown[] }
        | null
        | undefined;
      const n = envelope?.entries?.length ?? 0;
      return `${n} scope entr${n === 1 ? 'y' : 'ies'}`;
    }
    case 'ambiguity_card': {
      const prompt = tryStr(content.prompt) ?? tryStr(content.question);
      if (!prompt) return null;
      return prompt.length > 60 ? `${prompt.slice(0, 60)}…` : prompt;
    }
    case 'result_card': {
      const tool = tryStr(content.tool);
      return tool ? `Result: ${tool}` : 'Result';
    }
    case 'explain_answer_card': {
      const answer = tryStr(content.answer) ?? tryStr(content.text);
      if (!answer) return null;
      return answer.length > 60 ? `${answer.slice(0, 60)}…` : answer;
    }
    default:
      return null;
  }
}
